/**
 * Profile API Service
 *
 * Handles all profile-related API calls including 2FA, sessions,
 * login history, security events, and avatar management.
 */

import { api } from './client';
import { ApiResponse, UserPreferences, User } from '@/types';
import {
  TwoFactorSetupData,
  TwoFactorStatus,
  TwoFactorEnableRequest,
  TwoFactorDisableRequest,
  TwoFactorVerificationRequest,
  UserSession,
  SessionsResponse,
  LoginHistoryResponse,
  LoginHistoryQueryParams,
  SecurityEventsResponse,
  SecurityEventsQueryParams,
  SecurityAlert,
  SecurityAlertsResponse,
  SecurityAlertsQueryParams,
  ProfileUpdateRequest,
  PasswordChangeRequest,
  EmailChangeRequest,
  AvatarUploadResponse,
} from '@/types/profile';

interface CurrentSessionResponse {
  session?: UserSession;
}

export interface BulkSessionTerminationResponse {
  revokedCount: number;
}

type SecurityAlertApi = Omit<SecurityAlert, 'id' | 'timestamp'> & {
  _id?: string;
  id?: string;
  timestamp: Date | string;
  read?: boolean;
  isRead?: boolean;
  unread?: boolean;
  readAt?: Date | string | null;
  status?: string;
};

interface SecurityAlertsApiEnvelope {
  alerts?: SecurityAlertApi[];
  total?: number;
  unreadCount?: number;
}

type SecurityAlertsApiResponse = SecurityAlertApi[] | SecurityAlertsApiEnvelope;

const normalizeDate = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value);

const normalizeSecurityAlert = (alert: SecurityAlertApi): SecurityAlert => ({
  id: alert.id ?? alert._id ?? '',
  eventType: alert.eventType,
  severity: alert.severity,
  timestamp: normalizeDate(alert.timestamp),
  ipAddress: alert.ipAddress,
  userAgent: alert.userAgent,
  metadata: alert.metadata,
});

const getAlertUnreadState = (alert: SecurityAlertApi): boolean | undefined => {
  if (typeof alert.unread === 'boolean') return alert.unread;
  if (typeof alert.isRead === 'boolean') return !alert.isRead;
  if (typeof alert.read === 'boolean') return !alert.read;
  if (alert.readAt !== undefined) return alert.readAt === null;

  if (typeof alert.status === 'string') {
    const status = alert.status.toLowerCase();
    if (status === 'unread' || status === 'new' || status === 'pending') return true;
    if (status === 'read' || status === 'seen' || status === 'dismissed' || status === 'resolved') {
      return false;
    }
  }

  return undefined;
};

const deriveUnreadCount = (alerts: SecurityAlertApi[]): number =>
  alerts.reduce((count, alert) => count + (getAlertUnreadState(alert) === true ? 1 : 0), 0);

/**
 * Two-Factor Authentication Service
 */
export class TwoFactorService {
  /**
   * Get current 2FA status
   */
  static async getStatus(): Promise<ApiResponse<TwoFactorStatus>> {
    return api.get('/2fa/status');
  }

  /**
   * Initialize 2FA setup - get QR code and secret
   */
  static async setup(): Promise<ApiResponse<TwoFactorSetupData>> {
    return api.post('/2fa/setup');
  }

  /**
   * Enable 2FA with verification code
   */
  static async enable(data: TwoFactorEnableRequest): Promise<ApiResponse<void>> {
    return api.post('/2fa/enable', data);
  }

  /**
   * Disable 2FA
   */
  static async disable(data: TwoFactorDisableRequest): Promise<ApiResponse<void>> {
    return api.post('/2fa/disable', data);
  }

  /**
   * Generate new backup codes
   */
  static async generateBackupCodes(): Promise<ApiResponse<{ backupCodes: string[] }>> {
    return api.get('/2fa/backup-codes');
  }

  /**
   * Verify 2FA code (during login or sensitive operations)
   */
  static async verify(verification: TwoFactorVerificationRequest): Promise<ApiResponse<void>> {
    return api.post('/2fa/verify', verification);
  }
}

/**
 * Session Management Service
 */
export class SessionService {
  /**
   * Get all active sessions
   */
  static async getSessions(): Promise<ApiResponse<SessionsResponse>> {
    return api.get('/sessions');
  }

  /**
   * Get current session details
   */
  static async getCurrentSession(): Promise<ApiResponse<UserSession>> {
    const response = await api.get<CurrentSessionResponse>('/sessions/current');
    return {
      ...response,
      data: response.data?.session,
    };
  }

  /**
   * Terminate a specific session
   */
  static async terminateSession(sessionId: string): Promise<ApiResponse<void>> {
    return api.delete(`/sessions/${sessionId}`);
  }

  /**
   * Terminate all sessions except current
   */
  static async terminateAllSessions(): Promise<ApiResponse<BulkSessionTerminationResponse>> {
    return api.delete('/sessions');
  }
}

/**
 * Activity History Service
 */
export class ActivityHistoryService {
  private static getOffset(params: { page?: number; limit?: number; offset?: number }): number | undefined {
    if (params.offset !== undefined) return params.offset;
    if (params.page && params.limit) return Math.max(params.page - 1, 0) * params.limit;
    return undefined;
  }

  /**
   * Get login history
   */
  static async getLoginHistory(
    params: LoginHistoryQueryParams = {}
  ): Promise<ApiResponse<LoginHistoryResponse>> {
    const queryParams = new URLSearchParams();
    const offset = ActivityHistoryService.getOffset(params);

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (offset !== undefined) queryParams.append('offset', offset.toString());

    return api.get(`/users/me/login-history?${queryParams.toString()}`);
  }

  /**
   * Get security events
   */
  static async getSecurityEvents(
    params: SecurityEventsQueryParams = {}
  ): Promise<ApiResponse<SecurityEventsResponse>> {
    const queryParams = new URLSearchParams();
    const offset = ActivityHistoryService.getOffset(params);

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (offset !== undefined) queryParams.append('offset', offset.toString());
    params.eventTypes?.forEach((eventType) => queryParams.append('eventTypes', eventType));
    params.severity?.forEach((severity) => queryParams.append('severity', severity));

    return api.get(`/users/me/security-events?${queryParams.toString()}`);
  }

  /**
   * Get security alerts
   */
  static async getSecurityAlerts(
    params: SecurityAlertsQueryParams = {}
  ): Promise<ApiResponse<SecurityAlertsResponse>> {
    const queryParams = new URLSearchParams();

    if (params.days) queryParams.append('days', params.days.toString());

    const queryString = queryParams.toString();
    const response = await api.get<SecurityAlertsApiResponse>(
      `/users/me/security-alerts${queryString ? `?${queryString}` : ''}`
    );
    const rawData = response.data;
    const rawAlerts = Array.isArray(rawData) ? rawData : rawData?.alerts ?? [];
    const alerts = rawAlerts.map(normalizeSecurityAlert);
    const total = Array.isArray(rawData) ? alerts.length : rawData?.total ?? alerts.length;
    const unreadCount = Array.isArray(rawData)
      ? deriveUnreadCount(rawAlerts)
      : rawData?.unreadCount ?? deriveUnreadCount(rawAlerts);

    return {
      ...response,
      data: {
        alerts,
        total,
        unreadCount,
      },
    };
  }
}

/**
 * Profile Management Service
 */
export class ProfileService {
  /**
   * Update profile information
   */
  static async updateProfile(data: ProfileUpdateRequest): Promise<ApiResponse<void>> {
    return api.put('/users/me', data);
  }

  /**
   * Change password
   */
  static async changePassword(data: PasswordChangeRequest): Promise<ApiResponse<void>> {
    return api.put('/users/me/password', data);
  }

  /**
   * Change email address
   */
  static async changeEmail(data: EmailChangeRequest): Promise<ApiResponse<void>> {
    return api.put('/users/me/email', data);
  }

  /**
   * Upload avatar
   */
  static async uploadAvatar(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<AvatarUploadResponse>> {
    return api.upload('/users/me/avatar', file, onProgress, undefined, 'avatar');
  }

  /**
   * Delete avatar
   */
  static async deleteAvatar(): Promise<ApiResponse<void>> {
    return api.delete('/users/me/avatar');
  }

  /**
   * Delete user account (requires password)
   */
  static async deleteAccount(password: string): Promise<ApiResponse<void>> {
    return api.delete('/users/me', { data: { password } });
  }

  /**
   * Update user preferences (including home project)
   */
  static async updatePreferences(
    preferences: Partial<UserPreferences>
  ): Promise<ApiResponse<User>> {
    return api.put('/users/me/preferences', preferences);
  }

  /**
   * Set home project
   */
  static async setHomeProject(
    projectId: string
  ): Promise<ApiResponse<User>> {
    return api.put('/users/me/preferences', { homeProjectId: projectId });
  }

  /**
   * Clear home project
   */
  static async clearHomeProject(): Promise<ApiResponse<User>> {
    return api.put('/users/me/preferences', { homeProjectId: null });
  }

  /**
   * Set home page (sets both project and page together)
   */
  static async setHomePage(projectId: string, pageId: string): Promise<ApiResponse<User>> {
    return api.put('/users/me/preferences', { homeProjectId: projectId, homePageId: pageId });
  }

  /**
   * Clear home page (keeps home project)
   */
  static async clearHomePage(): Promise<ApiResponse<User>> {
    return api.put('/users/me/preferences', { homePageId: null });
  }

  /**
   * Clear both home project and home page
   */
  static async clearHomeProjectAndPage(): Promise<ApiResponse<User>> {
    return api.put('/users/me/preferences', { homeProjectId: null, homePageId: null });
  }
}

/**
 * Export all services as a unified namespace
 */
export const profileApi = {
  twoFactor: TwoFactorService,
  sessions: SessionService,
  activity: ActivityHistoryService,
  profile: ProfileService,
};

export default profileApi;
